import { useEffect } from "react";
import { StyledActionsContainer, StyledForm } from "./StyleComponents";
import { EditFormProps } from "./EditFormProps";
import { useEditForm } from "./useEditForm";
import { EditFormContents } from "./EditFormContents";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { Button } from "@kaltura/ds-react-components";
import { getComponentFromPath } from "@kaltura/mediaspace-shared-utils";
import { KmsTypeComponent } from "@kaltura/mediaspace-shared-types";
import { useEventHandler } from "@kaltura/mediaspace-shared-hooks";
import { FormProvider } from "react-hook-form";
import { ErrorBoundary } from "@kaltura/mediaspace-shared-ui";

/**
 * Entry edit form
 */
export const EditForm = (props: EditFormProps) => {
    const {
        showCancelButton = true,
        saveButtonLabel,
        onReadyForPublish,
        onCanSaveForm,
        showEditFormButtons = true,
    } = props;
    const handleCanSaveForm = useEventHandler(onCanSaveForm);
    const handleReadyForPublish = useEventHandler(onReadyForPublish);
    const editFormHookProps = useEditForm(props);
    const { processing, canSaveForm, readyForPublish, handleSubmitForm, handleCancel, handleReset } = editFormHookProps;
    useEffect(() => {
        // report readyForPublish when it changes, or the entryIds change (entry added or removed)
        // not using entryIds array as dependency - it changes every render
        handleReadyForPublish?.(props.entryIds, readyForPublish);
    }, [handleReadyForPublish, props.entryIds.length, readyForPublish]);
    useEffect(() => {
        handleCanSaveForm?.(canSaveForm);
    }, [handleCanSaveForm, canSaveForm]);

    const handleFormReset = (e: React.FormEvent) => {
        e.preventDefault();
        e.stopPropagation();
        handleReset();
    };

    return (
        <StyledForm
            showEditFormButtons={showEditFormButtons}
            id={props.formId}
            onSubmit={handleSubmitForm}
            onReset={handleFormReset}
        >
            <EditFormContents {...props} {...editFormHookProps} />
            <FormProvider {...editFormHookProps.useFormHookMethods}>
                {props.components?.map((component: KmsTypeComponent, index: number) => {
                    const Component = getComponentFromPath(component.component);

                    if (!Component) {
                        return null;
                    }

                    const { fieldsStructure, ...componentProps } = component.props;

                    return (
                        <ErrorBoundary fallback={<></>} key={index}>
                            <Component {...componentProps} entryIds={props.entryIds}>
                                {fieldsStructure && (
                                    <EditFormContents
                                        {...props}
                                        {...editFormHookProps}
                                        fieldsStructure={fieldsStructure}
                                    />
                                )}
                            </Component>
                        </ErrorBoundary>
                    );
                })}
            </FormProvider>
            {showEditFormButtons && (
                <StyledActionsContainer>
                    {showCancelButton && (
                        <Button
                            onClick={handleCancel}
                            disabled={processing}
                            size={"medium"}
                            variant={"borderless"}
                            color={"secondary"}
                        >
                            {translate("Cancel")}
                        </Button>
                    )}
                    <Button
                        type="submit"
                        loading={processing}
                        disabled={!canSaveForm || processing}
                        size={"medium"}
                        variant={"pill"}
                    >
                        {saveButtonLabel ?? translate("Save")}
                    </Button>
                </StyledActionsContainer>
            )}
        </StyledForm>
    );
};

export default EditForm;
